import { UiSchema } from '@rjsf/core';
import useAuth from 'hooks/useAuth';
import { JSONSchema7 } from 'json-schema';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import { User } from 'types/user';
import {
  GET_GENDERORIENTED_OPTIONS,
  GET_GENDER_OPTIONS,
  GET_RELATIONSHIP_OPTIONS,
  GET_SENTIMENTAL_STATUS_OPTIONS,
  GET_SEX_OPTIONS
} from 'utils/constants';
import JsonFormContainer from '../JsonFormContainer';
import PhoneNumberWidget from '../widgets/PhoneNumberWidget';
import Actions from './Actions';

const uiSchema: UiSchema = {
  personalInformation: {
    firstName: { 'ui:column': 6 },
    lastName: { 'ui:column': 6 },
    address: { 'ui:column': 6 },
    postalCode: { 'ui:column': 6 },
    phoneNumber: { 'ui:column': 6, 'ui:widget': PhoneNumberWidget },
    sentimentalStatus: { 'ui:column': 6 },
    age: { 'ui:column': 6 },
    gender: { 'ui:column': 6 },
    sex: { 'ui:column': 6 },
    genderOrientation: { 'ui:column': 6 }
  },
  emergencyContact: {
    firstName: { 'ui:column': 6 },
    lastName: { 'ui:column': 6 },
    email: { 'ui:column': 6 },
    phoneNumber: { 'ui:column': 6, 'ui:widget': PhoneNumberWidget }
  }
};

interface Props {
  user: User;
  refetchUser: () => void;
  onPrevious?: () => void;
  onSubmitSuccess: () => void;
}

export default function PersonalInformation({ user, refetchUser, onPrevious, onSubmitSuccess }: Props) {
  const intl = useIntl();
  const schema: JSONSchema7 = {
    type: 'object',
    properties: {
      personalInformation: {
        title: intl.formatMessage({ defaultMessage: 'Personal Information' }),
        type: 'object',
        properties: {
          firstName: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'First name' })
          },
          lastName: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Last name' })
          },
          email: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Email' })
          },
          address: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Address' })
          },
          postalCode: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Postal Code' })
          },
          phoneNumber: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Cell Phone Number' })
          },
          sentimentalStatus: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Current Status' }),
            enum: GET_SENTIMENTAL_STATUS_OPTIONS()
          },
          age: {
            type: 'number',
            title: intl.formatMessage({ defaultMessage: 'What is your age' })
          },
          gender: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Gender(optional)' }),
            enum: GET_GENDER_OPTIONS()
          },
          sex: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Sex(optional)' }),
            enum: GET_SEX_OPTIONS()
          },
          genderOrientation: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Gender orientation(optional)' }),
            enum: GET_GENDERORIENTED_OPTIONS()
          }
        }
      },
      emergencyContact: {
        title: intl.formatMessage({ defaultMessage: 'Emergency Contact' }),
        type: 'object',
        properties: {
          firstName: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'First name' })
          },
          lastName: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Last name' })
          },
          relationship: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Relationship' }),
            enum: GET_RELATIONSHIP_OPTIONS()
          },
          email: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Email' })
          },
          phoneNumber: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Cell Phone Number' })
          }
        }
      }
    }
  };
  const [updatedData, setUpdateData] = useState<any>(null);

  const formData = {
    personalInformation: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      address: user.address,
      postalCode: user.zipCode,
      phoneNumber: user.client.phoneNumber,
      sentimentalStatus: user.client.sentimentalStatus,
      age: Number(user.client.age),
      gender: user.client.gender,
      sex: user.sex,
      genderOrientation: user.genderOrientation
    },
    emergencyContact: user.client.emergencyContact
  };

  const { request } = useAuth();
  const updateUserMutation = useMutation((variables: any) => request.put(`/user/${user.id}`, variables), {
    onSuccess: () => {
      onSubmitSuccess();
      refetchUser();
    }
  });

  const handleSubmit = ({ formData }) => {
    setUpdateData(formData);
    const data = {
      adddress: formData.personalInformation.address,
      postalCode: formData.personalInformation.postalCode,
      client: {
        phoneNumber: formData.personalInformation.phoneNumber,
        sentimentalStatus: formData.personalInformation.sentimentalStatus,
        age: formData.personalInformation.age,
        gender: formData.personalInformation.gender,
        emergencyContact: formData.emergencyContact
      },

      sex: formData.personalInformation.sex,
      genderOrientation: formData.personalInformation.genderOrientation
    };

    updateUserMutation.mutate(data);
  };

  return (
    <JsonFormContainer noValidate schema={schema} formData={updatedData ?? formData} uiSchema={uiSchema} onSubmit={handleSubmit}>
      <Actions onPrevious={onPrevious} disabled={updateUserMutation.isLoading} />
    </JsonFormContainer>
  );
}
